import React, { useState } from 'react';
import { Card, Button, Alert, Spinner, Accordion } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import AppBadge from 'shared/badges/app-badge';
import {
  useAdminMunicipalitySqlSchema,
  useRunAdminMunicipalitySqlSchema,
  useAdminMunicipalitySqlSchemaDriftEmail,
  useAdminMunicipalitySqlSchemaDriftScripts,
  useDownloadAdminMunicipalitySqlSchemaDriftScripts
} from 'api/hooks/use-admin-municipality-sql-schema';

const formatCheckedAt = (iso) => {
  if (!iso) return 'never';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
};

const StatusBadge = ({ check }) => {
  if (!check?.checked_at) return <AppBadge variant="secondary">Not checked yet</AppBadge>;
  if (check.has_issues) return <AppBadge variant="danger">Issues</AppBadge>;
  return <AppBadge variant="success">Clean</AppBadge>;
};

const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_err) {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <Button variant="light" size="sm" className="text-primary py-0" onClick={handleCopy} title={`Copy ${label}`}>
      <FontAwesomeIcon icon={copied ? faCheck : faCopy} className={`me-1 ${copied ? 'text-success' : ''}`} />
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
};

const ItContactEmail = ({ email }) => {
  const [copied, setCopied] = useState(false);

  if (!email) return <span className="text-muted">No IT contact on file</span>;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_err) {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <span className="d-inline-flex align-items-center gap-2">
      <span>{email}</span>
      <Button variant="light" size="sm" className="text-primary py-0" onClick={handleCopy} title="Copy email">
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} className={copied ? 'text-success' : ''} />
      </Button>
    </span>
  );
};

// Collapses the per-section drift into a single, module-agnostic view keyed by
// table — the same way the IT email is summarized. Missing tables and column
// changes seen in more than one section are deduplicated into one entry.
const buildDriftSummary = (sections = []) => {
  const missingTables = new Set();
  const byTable = new Map();

  sections.forEach((section) => {
    (section.missing_tables || []).forEach((t) => missingTables.add(t));
    (section.tables_with_column_drift || []).forEach((drift) => {
      if (!byTable.has(drift.table)) {
        byTable.set(drift.table, { missing: new Set(), extra: new Set() });
      }
      const entry = byTable.get(drift.table);
      (drift.missing || []).forEach((c) => entry.missing.add(c));
      (drift.extra || []).forEach((c) => entry.extra.add(c));
    });
  });

  return {
    missingTables: [...missingTables].sort(),
    columnDrift: [...byTable.entries()]
      .map(([table, v]) => ({ table, missing: [...v.missing].sort(), extra: [...v.extra].sort() }))
      .sort((a, b) => a.table.localeCompare(b.table))
  };
};

const MunicipalitySqlSchema = ({ municipality }) => {
  const municipalityId = municipality.id;

  const { data: check, isLoading, error, refetch } = useAdminMunicipalitySqlSchema({ municipalityId });
  const { mutate: runCheck, isLoading: running } = useRunAdminMunicipalitySqlSchema({
    municipalityId,
    options: {
      onSuccess: (data) => {
        toast.success(data?.message || 'SQL schema check queued');
        // Wait a few seconds for the worker to land, then refetch.
        setTimeout(() => refetch(), 5000);
      },
      onError: (err) => toast.error(err?.response?.data?.message || 'Failed to queue SQL schema check')
    }
  });

  const [email, setEmail] = useState(null);
  const { mutate: draftEmail, isLoading: drafting } = useAdminMunicipalitySqlSchemaDriftEmail({
    municipalityId,
    options: {
      onSuccess: (data) => setEmail(data),
      onError: (err) => toast.error(err?.response?.data?.message || 'Failed to draft IT email')
    }
  });

  const [scripts, setScripts] = React.useState(null);
  const { mutate: getScripts, isLoading: loadingScripts } = useAdminMunicipalitySqlSchemaDriftScripts({
    municipalityId,
    options: {
      onSuccess: (data) => setScripts(data?.scripts || []),
      onError: (err) => toast.error(err?.response?.data?.message || 'Failed to load SQL scripts')
    }
  });

  const zipFilename = `${(municipality.name || 'municipality').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_+|_+$)/g, '')}_drift_SQL.zip`;
  const { mutate: downloadZip, isLoading: downloading } = useDownloadAdminMunicipalitySqlSchemaDriftScripts({
    municipalityId,
    options: {
      onError: (err) => toast.error(err?.response?.data?.message || 'Failed to download SQL scripts')
    }
  });

  const copyEmail = () => {
    if (!email) return;
    const text = `Subject: ${email.subject}\n\n${email.body}`;
    navigator.clipboard.writeText(text).then(
      () => toast.success('Email copied to clipboard'),
      () => toast.error('Could not copy to clipboard')
    );
  };

  const copyScript = (script) => {
    navigator.clipboard.writeText(script.body).then(
      () => toast.success(`Copied ${script.filename}`),
      () => toast.error('Could not copy to clipboard')
    );
  };

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">Error loading SQL schema check: {error.message}</Alert>;
  }

  const sectionsWithIssues = (check?.sections || []).filter(
    (s) => s.missing_tables?.length > 0 || s.tables_with_column_drift?.length > 0
  );
  const cleanSections = (check?.sections || []).filter(
    (s) => !s.missing_tables?.length && !s.tables_with_column_drift?.length
  );
  const driftSummary = buildDriftSummary(check?.sections);
  const hasDrift = driftSummary.missingTables.length > 0 || driftSummary.columnDrift.length > 0;

  return (
    <>
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">SQL Schema Health</h5>
            <div className="d-flex gap-2">
              {check?.has_issues && (
                <>
                  <Button
                    variant="light"
                    className="text-primary"
                    size="sm"
                    onClick={() => draftEmail()}
                    disabled={drafting}
                  >
                    {drafting ? <Spinner animation="border" size="sm" /> : 'Draft IT email'}
                  </Button>
                  <Button
                    variant="light"
                    className="text-primary"
                    size="sm"
                    onClick={() => getScripts()}
                    disabled={loadingScripts}
                  >
                    {loadingScripts ? <Spinner animation="border" size="sm" /> : 'Get SQL scripts'}
                  </Button>
                  <Button
                    variant="light"
                    className="text-primary"
                    size="sm"
                    onClick={() => downloadZip(zipFilename)}
                    disabled={downloading}
                  >
                    {downloading ? <Spinner animation="border" size="sm" /> : 'Download .zip'}
                  </Button>
                </>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={() => runCheck()}
                disabled={running}
              >
                {running ? <Spinner animation="border" size="sm" /> : 'Re-run check'}
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <dl className="row mb-0">
            <dt className="col-sm-3">Status:</dt>
            <dd className="col-sm-9"><StatusBadge check={check} /></dd>
            <dt className="col-sm-3">Last checked:</dt>
            <dd className="col-sm-9">{formatCheckedAt(check?.checked_at)}</dd>
            <dt className="col-sm-3">Manifest version:</dt>
            <dd className="col-sm-9">
              {check?.manifest_version
                ? <code>{check.manifest_version}</code>
                : <span className="text-muted">unknown</span>
              }
            </dd>
            <dt className="col-sm-3">Missing tables:</dt>
            <dd className="col-sm-9">{check?.total_missing_tables ?? 0}</dd>
            <dt className="col-sm-3">Missing columns:</dt>
            <dd className="col-sm-9">{check?.total_missing_columns ?? 0}</dd>
          </dl>
        </Card.Body>
      </Card>

      {!check?.checked_at && (
        <Alert variant="info">
          No SQL schema check has run yet for this municipality. Click "Re-run check" to start one.
        </Alert>
      )}

      {email && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Draft email for IT</h5>
          </Card.Header>
          <Card.Body>
            <dl className="row mb-3">
              <dt className="col-sm-2">To:</dt>
              <dd className="col-sm-10"><ItContactEmail email={municipality.it_contact_email} /></dd>
              <dt className="col-sm-2">Subject:</dt>
              <dd className="col-sm-10 d-flex align-items-center gap-2">
                <span>{email.subject}</span>
                <CopyButton text={email.subject} label="subject" />
              </dd>
            </dl>
            <div className="d-flex justify-content-end mb-1">
              <CopyButton text={email.body} label="body" />
            </div>
            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{email.body}</pre>
          </Card.Body>
        </Card>
      )}

      {scripts && (
        <Card className="mb-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                SQL scripts to send{' '}
                <AppBadge variant="secondary">{scripts.length}</AppBadge>
              </h5>
              {scripts.length > 0 && (
                <Button
                  variant="light"
                  className="text-primary"
                  size="sm"
                  onClick={() => downloadZip(zipFilename)}
                  disabled={downloading}
                >
                  {downloading ? <Spinner animation="border" size="sm" /> : 'Download .zip'}
                </Button>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {scripts.length === 0 ? (
              <Alert variant="info" className="mb-0">
                No new or updated scripts to send for this municipality.
              </Alert>
            ) : (
              <Accordion>
                {scripts.map((script) => (
                  <Accordion.Item eventKey={script.table} key={script.table}>
                    <Accordion.Header>
                      <code className="me-2">{script.filename}</code>
                      <AppBadge variant={script.status === 'new' ? 'success' : 'warning'} className="me-2">
                        {script.status}
                      </AppBadge>
                      {script.source === 'custom' && (
                        <AppBadge variant="info">custom override</AppBadge>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="d-flex justify-content-end mb-2">
                        <Button variant="light" size="sm" onClick={() => copyScript(script)}>Copy</Button>
                      </div>
                      <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{script.body}</pre>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}
          </Card.Body>
        </Card>
      )}

      {hasDrift && (
        <Card className="mb-4">
          <Card.Header><h5 className="mb-0">Drift summary (all sections)</h5></Card.Header>
          <Card.Body>
            <Accordion>
              {driftSummary.missingTables.length > 0 && (
                <Accordion.Item eventKey="missing-tables">
                  <Accordion.Header>
                    <span className="me-2">Missing tables</span>
                    <AppBadge variant="danger">{driftSummary.missingTables.length}</AppBadge>
                  </Accordion.Header>
                  <Accordion.Body>
                    <ul className="mb-0">
                      {driftSummary.missingTables.map((t) => (
                        <li key={t}><code>{t}</code></li>
                      ))}
                    </ul>
                  </Accordion.Body>
                </Accordion.Item>
              )}
              {driftSummary.columnDrift.map((drift) => (
                <Accordion.Item eventKey={drift.table} key={drift.table}>
                  <Accordion.Header>
                    <code className="me-2">{drift.table}</code>
                    {drift.missing.length > 0 && (
                      <AppBadge variant="danger" className="me-2">
                        {drift.missing.length} missing column{drift.missing.length === 1 ? '' : 's'}
                      </AppBadge>
                    )}
                    {drift.extra.length > 0 && (
                      <AppBadge variant="warning">
                        {drift.extra.length} extra column{drift.extra.length === 1 ? '' : 's'}
                      </AppBadge>
                    )}
                  </Accordion.Header>
                  <Accordion.Body>
                    {drift.missing.length > 0 && (
                      <div className="mb-2">
                        <span className="text-danger">Missing columns:</span>{' '}
                        {drift.missing.map((c) => <code key={c} className="me-1">{c}</code>)}
                      </div>
                    )}
                    {drift.extra.length > 0 && (
                      <div>
                        <span className="text-warning">Extra columns:</span>{' '}
                        {drift.extra.map((c) => <code key={c} className="me-1">{c}</code>)}
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Card.Body>
        </Card>
      )}

      {sectionsWithIssues.length > 0 && (
        <Card className="mb-4">
          <Card.Header><h5 className="mb-0">Sections with issues</h5></Card.Header>
          <Card.Body>
            <Accordion>
              {sectionsWithIssues.map((section) => (
                <Accordion.Item eventKey={String(section.section_id)} key={section.section_id}>
                  <Accordion.Header>
                    <span className="me-2">{section.section_abbreviation}</span>
                    <code className="text-muted me-2">{section.section_folder}</code>
                    {section.missing_tables?.length > 0 && (
                      <AppBadge variant="danger" className="me-2">
                        {section.missing_tables.length} missing table{section.missing_tables.length === 1 ? '' : 's'}
                      </AppBadge>
                    )}
                    {section.tables_with_column_drift?.length > 0 && (
                      <AppBadge variant="warning">
                        {section.tables_with_column_drift.length} drifted table{section.tables_with_column_drift.length === 1 ? '' : 's'}
                      </AppBadge>
                    )}
                  </Accordion.Header>
                  <Accordion.Body>
                    {section.missing_tables?.length > 0 && (
                      <div className="mb-3">
                        <strong>Missing tables:</strong>
                        <ul className="mb-0">
                          {section.missing_tables.map((t) => (
                            <li key={t}><code>{t}</code></li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {section.tables_with_column_drift?.length > 0 && (
                      <div>
                        <strong>Tables with column drift:</strong>
                        <ul className="mb-0">
                          {section.tables_with_column_drift.map((drift) => (
                            <li key={drift.table}>
                              <code>{drift.table}</code>
                              {drift.missing?.length > 0 && (
                                <div className="ms-3">
                                  <span className="text-danger">Missing:</span> {drift.missing.map((c) => <code key={c} className="me-1">{c}</code>)}
                                </div>
                              )}
                              {drift.extra?.length > 0 && (
                                <div className="ms-3">
                                  <span className="text-warning">Extra:</span> {drift.extra.map((c) => <code key={c} className="me-1">{c}</code>)}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Card.Body>
        </Card>
      )}

      {cleanSections.length > 0 && (
        <Card>
          <Card.Header><h5 className="mb-0">Clean sections</h5></Card.Header>
          <Card.Body>
            <div className="d-flex flex-wrap gap-2">
              {cleanSections.map((section) => (
                <AppBadge key={section.section_id} variant="success">
                  {section.section_abbreviation}
                </AppBadge>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default MunicipalitySqlSchema;
